import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { CardListSkeleton } from "../components/Skeletons";
import SlaBeacon, { jrBeaconLevel } from "../components/SlaBeacon";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import ScopeOfWorksField from "../components/jobs/ScopeOfWorksField";
import ClientFilterSelect, { matchesClientFilter, clientFilterFromParams, applyClientFilterParam } from "../components/ClientFilterSelect";
import { ClipboardText, Plus, MagnifyingGlass, Funnel, Eye, CurrencyGbp, Clock, MapPin, User, Trash, Paperclip, File as FileIcon, X, Warning, CheckSquare, Square, Bookmark, Hand, UserSwitch, BellRinging, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Switch } from "../components/ui/switch";
import { FileDropZone } from "../components/FileDropZone";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import { AiBriefButton } from "../components/AiFeatures";
import { detectService } from "../lib/serviceDetect";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ClientPropertyPicker from "../components/ClientPropertyPicker";
import AccessNotesField from "../components/AccessNotesField";
import RepeatFaultBanner, { splitFlatAddress } from "../components/RepeatFaultBanner";
import SLAStatusBadge, { slaRowAccent } from "../components/SLAStatusBadge";

const STATUS_MAP = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  awaiting_quotes: { label: "Awaiting Quotes", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
  quotes_received: { label: "Quotes Received", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" },
  submitted_to_client: { label: "Quote with Client", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400" },
  client_approved: { label: "Client Approved", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" },
  converting: { label: "Converting", color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-400" },
  converted: { label: "Converted", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  declined: { label: "Declined", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
  on_hold: { label: "On Hold", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400" },
};
const PRIORITY_MAP = { low: "bg-slate-100 text-slate-700", medium: "bg-blue-100 text-blue-700", high: "bg-amber-100 text-amber-700", urgent: "bg-red-100 text-red-700" };
const CATEGORIES = ["general", "maintenance", "refurbishment", "new_build", "renovation", "fit_out", "planned_works", "reactive_repair", "inspection", "electrical", "plumbing", "roofing", "structural", "mould_eradication", "other"];

export default function JobRequestsPage() {
  const { user } = useAuth();
  const { byId: waitingById } = useClientWaiting();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jobRequests, setJobRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");  // "open" | "converted" | "all"
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [requesterFilter, setRequesterFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState(() => {
    const t = searchParams.get("team");
    if (!t) return "all";
    return t === "unclaimed" ? "__unclaimed__" : t;
  });
  const [adminUsers, setAdminUsers] = useState([]);
  const [myWork, setMyWork] = useState(() => localStorage.getItem("fixli_my_work") === "1");
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("fixli_jr_sort") || "newest");
  const [alertCfg, setAlertCfg] = useState({ enabled: true, hours: 4 });
  const [alertOpen, setAlertOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState(() => clientFilterFromParams(new URLSearchParams(window.location.search)));
  const [missingScopeOnly, setMissingScopeOnly] = useState(false);
  const changeClientFilter = (v) => { setClientFilter(v); const next = applyClientFilterParam(new URLSearchParams(window.location.search), v); window.history.replaceState(null, "", `${window.location.pathname}${next.toString() ? `?${next}` : ""}`); };
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [serviceManual, setServiceManual] = useState(false);
  const [serviceSuggested, setServiceSuggested] = useState(false);
  const [form, setForm] = useState({
    title: "", client_name: "", client_email: "", client_phone: "", client_company: "",
    client_id: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "",
    property_address: "", property_id: "", property_name: "", add_to_portfolio: false, what3words: "", access_notes: "", request_details: "", scope_of_works: "", category: "general", service_required: "", priority: "medium",
    requested_completion_date: "", estimated_budget: "", notice: { ...EMPTY_NOTICE },
  });
  const [formAttachments, setFormAttachments] = useState([]);

  // Auto-suggest the service from title + description unless the admin picked one manually
  useEffect(() => {
    if (!showCreate || serviceManual) return;
    const detected = detectService(`${form.title} ${form.request_details}`, serviceOptions);
    if (detected && detected !== form.service_required) {
      setForm(prev => ({ ...prev, service_required: detected }));
      setServiceSuggested(true);
    }
  }, [form.title, form.request_details, serviceOptions, serviceManual, showCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showCreate) { setServiceManual(false); setServiceSuggested(false); }
  }, [showCreate]);
  const [stats, setStats] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [selectedClientContacts, setSelectedClientContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [workTemplates, setWorkTemplates] = useState([]);

  const fetchJRs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("tab", tab);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (search) params.append("search", search);
      params.append("limit", "1000");
      const { data } = await API.get(`/job-requests?${params}`);
      setJobRequests(data.job_requests || []);
      setTotal(data.total || 0);
    } catch {}
    finally { setLoading(false); }
  }, [tab, statusFilter, priorityFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await API.get("/job-requests/stats");
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { fetchJRs(); fetchStats(); }, [fetchJRs, fetchStats]);

  useEffect(() => {
    API.get("/users?role=admin").then(r => setAdminUsers(r.data || [])).catch(() => {});
    API.get("/suppliers/service-options").then(({ data }) => setServiceOptions(data.services || [])).catch(() => {});
  }, []);

  useEffect(() => {
    API.get("/clients?limit=500").then(({ data }) => setAllClients(data.clients || [])).catch(() => {});
    API.get("/work-templates?template_type=request").then(({ data }) => setWorkTemplates(data.templates || [])).catch(() => {});
  }, []);

  const applyTemplate = (templateId) => {
    if (!templateId || templateId === "__none__") return;
    const tpl = workTemplates.find(t => t.id === templateId);
    if (!tpl || !tpl.fields) return;
    setForm(prev => ({ ...prev, ...tpl.fields }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  // Build unique requesters for filter dropdown
  const uniqueRequesters = [...new Map(
    jobRequests
      .filter(jr => jr.requester_contact_name)
      .map(jr => [jr.requester_contact_name, { name: jr.requester_contact_name, color: jr.requester_contact_color || "#3b82f6" }])
  ).values()];

  // Apply requester + team filters client-side; unclaimed requests float to the top
  const isUnclaimed = (jr) => !jr.actioned_by && !jr.converted_job_id && !["converted", "declined"].includes(jr.status);
  const waitingInfo = (jr) => {
    const hrs = (Date.now() - new Date(jr.created_at).getTime()) / 36e5;
    const label = hrs < 1 ? "<1h" : hrs < 48 ? `${Math.floor(hrs)}h` : `${Math.floor(hrs / 24)}d`;
    const cls = hrs >= 24 ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
      : hrs >= 4 ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
      : "bg-muted text-muted-foreground";
    return { label, cls };
  };
  const uniqueActioners = [...new Set(jobRequests.filter(jr => jr.actioned_by_name).map(jr => jr.actioned_by_name))].sort();
  const uniqueAdmins = adminUsers.reduce((acc, a) => {
    if (a.active !== false && !acc.some(x => x.name === a.name)) acc.push(a);
    return acc;
  }, []);
  const JR_DONE = new Set(["converted", "declined", "cancelled"]);
  const missingScopeCount = jobRequests.filter(jr => !(jr.scope_of_works || "").trim() && !JR_DONE.has(jr.status)).length;
  const filteredJRs = jobRequests
    .filter(jr => matchesClientFilter(jr, clientFilter))
    .filter(jr => !missingScopeOnly || (!(jr.scope_of_works || "").trim() && !JR_DONE.has(jr.status)))
    .filter(jr => requesterFilter === "all" || jr.requester_contact_name === requesterFilter)
    .filter(jr => teamFilter === "all" || (teamFilter === "__unclaimed__" ? isUnclaimed(jr) : jr.actioned_by_name === teamFilter))
    .filter(jr => !myWork || jr.actioned_by === String(user?._id))
    .sort((a, b) => {
      const unclaimedDiff = (isUnclaimed(b) ? 1 : 0) - (isUnclaimed(a) ? 1 : 0);
      if (unclaimedDiff !== 0) return unclaimedDiff;
      if (isUnclaimed(a)) {
        const urgentDiff = (b.priority === "urgent" ? 1 : 0) - (a.priority === "urgent" ? 1 : 0);
        if (urgentDiff !== 0) return urgentDiff;
      }
      const da = new Date(a.created_at).getTime() || 0;
      const db = new Date(b.created_at).getTime() || 0;
      return sortOrder === "oldest" ? da - db : db - da;
    });

  const changeSortOrder = (v) => { setSortOrder(v); localStorage.setItem("fixli_jr_sort", v); };

  const toggleMyWork = () => setMyWork(v => { const nv = !v; localStorage.setItem("fixli_my_work", nv ? "1" : "0"); return nv; });

  const claimJR = async (jr) => {
    try {
      await API.post(`/sla/job-requests/${jr.id}/action`, {});
      toast.success(`Claimed ${jr.reference || "request"}`);
      fetchJRs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not claim"); }
  };

  const handoverJR = async (jr, admin) => {
    try {
      await API.post("/sla/reassign", { entity_type: "job-requests", entity_id: jr.id, user_id: admin ? String(admin._id) : "" });
      toast.success(admin ? `Handed to ${admin.name}` : "Unclaimed");
      fetchJRs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign"); }
  };

  const loadAlertCfg = () => API.get("/scheduler/unclaimed-alert-settings").then(r => setAlertCfg(r.data)).catch(() => {});
  const saveAlertCfg = async () => {
    try {
      const { data } = await API.put("/scheduler/unclaimed-alert-settings", {
        enabled: alertCfg.enabled, hours: Number(alertCfg.hours),
        daily_summary_enabled: !!alertCfg.daily_summary_enabled,
        daily_summary_time: alertCfg.daily_summary_time || "08:00",
      });
      setAlertCfg(data);
      setAlertOpen(false);
      toast.success("Alert settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const handleSelectClient = (clientId) => {
    if (!clientId || clientId === "__none__") {
      setForm(prev => ({ ...prev, client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" }));
      setSelectedClientContacts([]);
      return;
    }
    const client = allClients.find(c => c.id === clientId);
    if (client) {
      setForm(prev => ({
        ...prev,
        client_id: client.id,
        client_name: `${client.first_name} ${client.last_name}`,
        client_email: client.email || "",
        client_phone: client.phone || "",
        client_company: client.company_name || "",
        requester_contact_id: "",
        requester_contact_name: "",
        requester_contact_color: "",
      }));
      setSelectedClientContacts(client.contacts || []);
    }
  };

  const handleSelectRequester = (contactId) => {
    if (!contactId || contactId === "__none__") {
      setForm(prev => ({ ...prev, requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", requester_contact_email: "", requester_contact_phone: "" }));
      return;
    }
    const contact = selectedClientContacts.find(c => c.id === contactId);
    if (contact) {
      setForm(prev => ({ ...prev, requester_contact_id: contact.id, requester_contact_name: `${contact.first_name} ${contact.last_name}`, requester_contact_color: contact.color || "#3b82f6", requester_contact_email: contact.email || "", requester_contact_phone: contact.phone || "" }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setCreating(true);
    try {
      const payload = { ...form, estimated_budget: parseFloat(form.estimated_budget) || 0, attachments: formAttachments };
      const { data } = await API.post("/job-requests", payload);
      toast.success(`Job request ${data.reference} created`);
      setShowCreate(false);
      setForm({ title: "", client_name: "", client_email: "", client_phone: "", client_company: "", client_id: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", property_address: "", what3words: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", access_notes: "", request_details: "", scope_of_works: "", category: "general", service_required: "", priority: "medium", requested_completion_date: "", estimated_budget: "", notice: { ...EMPTY_NOTICE } });
      setFormAttachments([]);
      setSelectedClientContacts([]);
      navigate(`/job-requests/${data.id}`);
    } catch { toast.error("Failed to create job request"); }
    finally { setCreating(false); }
  };

  const activeCount = stats?.by_status ? Object.entries(stats.by_status).filter(([k]) => !["converted", "declined"].includes(k)).reduce((s, [, v]) => s + v, 0) : 0;

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredJRs.length) setSelectedIds([]);
    else setSelectedIds(filteredJRs.map(jr => jr.id));
  };
  const handleBulkStatus = async (status) => {
    if (!selectedIds.length) return;
    setBulkUpdating(true);
    try {
      const { data } = await API.post("/bulk/job-requests/update-status", { jr_ids: selectedIds, status });
      toast.success(`${data.updated} request(s) updated to ${STATUS_MAP[status]?.label || status}`);
      setSelectedIds([]);
      fetchJRs(); fetchStats();
    } catch { toast.error("Failed to update"); }
    finally { setBulkUpdating(false); }
  };
  const handleBulkArchive = async () => {
    if (!selectedIds.length) return;
    setBulkUpdating(true);
    try {
      const { data } = await API.post("/bulk/job-requests/archive", { jr_ids: selectedIds });
      toast.success(`${data.archived} request(s) archived`);
      setSelectedIds([]);
      fetchJRs(); fetchStats();
    } catch { toast.error("Failed to archive"); }
    finally { setBulkUpdating(false); }
  };

  // Phase 258 — Bulk-claim ownership (Action SLA)
  const handleBulkAction = async () => {
    if (!selectedIds.length) return;
    setBulkUpdating(true);
    try {
      const { data } = await API.post("/sla/bulk-action", { entity_type: "job-requests", entity_ids: selectedIds });
      toast.success(`Actioned ${data.actioned || 0}` + (data.skipped ? ` • ${data.skipped} skipped (already actioned)` : ""));
      setSelectedIds([]);
      fetchJRs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkUpdating(false); }
  };

  return (
    <div data-testid="job-requests-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} total, {activeCount} active</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-job-request-btn">
          <Plus size={16} className="mr-1" /> New Request
        </Button>
      </div>

      {/* Top tabs (Phase 285): Open / Converted / All */}
      <div className="flex items-center gap-1 mb-4 border-b border-border" data-testid="jr-tabs">
        {[
          { key: "open",      label: "Open",      help: "Active requests still being worked on" },
          { key: "converted", label: "Converted", help: "Requests that have been turned into jobs" },
          { key: "all",       label: "All",       help: "Everything, newest first" },
        ].map(t => {
          const count = stats?.by_tab?.[t.key] ?? (t.key === "all" ? total : undefined);
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setStatusFilter("all"); }}
              title={t.help}
              data-testid={`jr-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                active
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
              {typeof count === "number" && (
                <span className={`ml-1.5 inline-block min-w-[1.5rem] text-center text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Status summary cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-6" data-testid="jr-stats-cards">
          {[
            { key: "draft", label: "Draft" },
            { key: "awaiting_quotes", label: "Awaiting Quotes" },
            { key: "quotes_received", label: "Quotes Received" },
            { key: "submitted_to_client", label: "Quote with Client" },
            { key: "client_approved", label: "Approved" },
          ].map(s => (
            <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? "all" : s.key)}
              className={`border rounded-md p-3 text-left card-lift ${statusFilter === s.key ? "border-primary bg-primary/5" : "border-border"}`}>
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-2xl font-bold font-[Chivo]">{stats.by_status?.[s.key] || 0}</p>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search requests..." className="pl-9" data-testid="jr-search" />
        </div>
        <ClientFilterSelect rows={jobRequests} value={clientFilter} onChange={changeClientFilter} className="w-[200px]" testid="jr-client-filter" />
        <button type="button" onClick={() => setMissingScopeOnly(v => !v)} className={`flex items-center gap-1 px-2.5 h-10 rounded-md border text-xs font-medium transition-colors ${missingScopeOnly ? "border-amber-400 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "border-border text-muted-foreground hover:border-amber-400/60"}`} title="Open requests without a scope of works" data-testid="jr-missing-scope-filter">
          Missing scope{missingScopeCount > 0 && <span className="ml-0.5 tabular-nums" data-testid="jr-missing-scope-count">({missingScopeCount})</span>}
        </button>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="jr-status-filter"><Funnel size={14} className="mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[130px]" data-testid="jr-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            {["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        {uniqueRequesters.length > 0 && (
          <Select value={requesterFilter} onValueChange={setRequesterFilter}>
            <SelectTrigger className="w-[180px]" data-testid="jr-requester-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requesters</SelectItem>
              {uniqueRequesters.map(r => (
                <SelectItem key={r.name} value={r.name}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 inline-block" style={{ backgroundColor: r.color }} />
                    {r.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-[190px]" data-testid="jr-team-filter"><User size={14} className="mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Team Members</SelectItem>
            <SelectItem value="__unclaimed__">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-600 inline-block" /> Unclaimed</span>
            </SelectItem>
            {uniqueActioners.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={changeSortOrder}>
          <SelectTrigger className="w-[140px]" data-testid="jr-sort-order"><Clock size={14} className="mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={myWork ? "default" : "outline"} className="h-10" onClick={toggleMyWork} data-testid="jr-my-work-toggle">
          <Hand size={14} className="mr-1" /> My Work
        </Button>
        <Popover open={alertOpen} onOpenChange={(o) => { setAlertOpen(o); if (o) loadAlertCfg(); }}>
          <PopoverTrigger asChild>
            <Button size="icon" variant="outline" className="h-10 w-10 shrink-0" title="Unclaimed alert settings" data-testid="jr-alert-settings-btn"><BellRinging size={15} /></Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72" data-testid="jr-alert-settings-popover">
            <p className="text-sm font-semibold mb-1">Unclaimed request alerts</p>
            <p className="text-xs text-muted-foreground mb-3">Pings all admins when a request sits unclaimed longer than the set hours (checked every 30 min, re-pinged daily until claimed).</p>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs">Enabled</Label>
              <Switch checked={alertCfg.enabled} onCheckedChange={(v) => setAlertCfg(c => ({ ...c, enabled: v }))} data-testid="jr-alert-enabled" />
            </div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs">Alert after (hours)</Label>
              <Input type="number" min={1} max={168} className="w-20 h-8" value={alertCfg.hours} onChange={(e) => setAlertCfg(c => ({ ...c, hours: e.target.value }))} data-testid="jr-alert-hours" />
            </div>
            <div className="border-t border-border pt-3 mb-3">
              <p className="text-xs font-semibold mb-2">Morning summary email</p>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Enabled</Label>
                <Switch checked={!!alertCfg.daily_summary_enabled} onCheckedChange={(v) => setAlertCfg(c => ({ ...c, daily_summary_enabled: v }))} data-testid="jr-summary-enabled" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Send at</Label>
                <Input type="time" className="w-28 h-8" value={alertCfg.daily_summary_time || "08:00"} onChange={(e) => setAlertCfg(c => ({ ...c, daily_summary_time: e.target.value }))} data-testid="jr-summary-time" />
              </div>
            </div>
            <Button size="sm" className="w-full" onClick={saveAlertCfg} data-testid="jr-alert-save">Save</Button>
          </PopoverContent>
        </Popover>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 rounded-lg bg-primary/5 border border-primary/20 animate-fade-in" data-testid="bulk-action-bar">
          <span className="text-xs font-semibold">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleBulkAction} disabled={bulkUpdating} data-testid="bulk-action-jrs">
              <Hand size={12} className="mr-1" /> Action selected
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleBulkStatus("awaiting_quotes")} disabled={bulkUpdating} data-testid="bulk-awaiting-quotes">Awaiting Quotes</Button>
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleBulkStatus("client_approved")} disabled={bulkUpdating} data-testid="bulk-approve">Approve</Button>
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleBulkStatus("on_hold")} disabled={bulkUpdating} data-testid="bulk-hold">On Hold</Button>
            <Button variant="outline" size="sm" className="text-xs h-7 text-destructive" onClick={handleBulkArchive} disabled={bulkUpdating} data-testid="bulk-archive">
              <Trash size={12} className="mr-1" /> Archive
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setSelectedIds([])} data-testid="bulk-clear">Clear</Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <CardListSkeleton rows={6} showHeader={false} />
      ) : filteredJRs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <ClipboardText size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-1">{requesterFilter !== "all" ? "No requests match this requester" : "No job requests yet"}</p>
          <p className="text-xs text-muted-foreground mb-4">{requesterFilter !== "all" ? "Try changing the requester filter." : "Create your first job request to start the quoting process."}</p>
          {requesterFilter === "all" && <Button onClick={() => setShowCreate(true)} data-testid="empty-create-jr-btn"><Plus size={16} className="mr-1" /> New Job Request</Button>}
        </div>
      ) : (
        <div className="border border-border rounded-md divide-y divide-border" data-testid="jr-list">
          {/* Select All Header */}
          <div className="flex items-center gap-3 px-4 py-2 bg-muted/30">
            <button onClick={toggleSelectAll} className="shrink-0" data-testid="jr-select-all">
              {selectedIds.length === filteredJRs.length && filteredJRs.length > 0 ? <CheckSquare size={18} className="text-primary" weight="fill" /> : <Square size={18} className="text-muted-foreground" />}
            </button>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{filteredJRs.length} request{filteredJRs.length !== 1 ? "s" : ""}</span>
          </div>
          {filteredJRs.map(jr => {
            const st = jr.converted_job_id
              ? STATUS_MAP.converted
              : (STATUS_MAP[jr.status] || STATUS_MAP.draft);
            const quoteCount = jr.quotes?.length || 0;
            const daysSinceUpdate = Math.floor((Date.now() - new Date(jr.updated_at || jr.created_at).getTime()) / (1000 * 60 * 60 * 24));
            const isStale = daysSinceUpdate >= 7 && !["converted", "declined"].includes(jr.status);
            const isOverdue = jr.requested_completion_date && new Date(jr.requested_completion_date) < new Date() && !["converted", "declined"].includes(jr.status);
            const isSelected = selectedIds.includes(jr.id);
            return (
              <div key={jr.id} className={`flex items-center gap-3 px-4 py-3.5 row-hover transition-colors ${isUnclaimed(jr) ? "border-l-2 border-l-red-500" : (slaRowAccent(jr) || (isStale ? "border-l-2 border-l-amber-400" : ""))} ${isSelected ? "bg-primary/5" : ""}`} data-testid={`jr-${jr.id}`}>
                <button onClick={(e) => { e.stopPropagation(); toggleSelect(jr.id); }} className="shrink-0" data-testid={`jr-check-${jr.id}`}>
                  {isSelected ? <CheckSquare size={18} className="text-primary" weight="fill" /> : <Square size={18} className="text-muted-foreground" />}
                </button>
                <Link to={`/job-requests/${jr.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      {(() => { const lvl = jrBeaconLevel(jr); return lvl ? <SlaBeacon level={lvl} /> : null; })()}
                      <span className="text-xs font-mono text-muted-foreground">{jr.reference}</span>
                      <ClientWaitingPill item={waitingById[jr.id]} testId={`jr-client-waiting-${jr.id}`} />
                      {jr.has_unseen_client_update && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold" title="The client added details to this request" data-testid={`jr-client-update-chip-${jr.id}`}><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Client added details</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                      {jr.request_type === "project" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-600 text-white font-bold tracking-wide" data-testid={`jr-project-${jr.id}`}>PROJECT</span>}
                      {isUnclaimed(jr) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white font-bold tracking-wide" data-testid={`jr-unclaimed-${jr.id}`}>UNCLAIMED</span>}
                      {isUnclaimed(jr) && (() => { const w = waitingInfo(jr); return (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5 ${w.cls}`} data-testid={`jr-waiting-${jr.id}`}>
                          <Clock size={9} /> waiting {w.label}
                        </span>
                      ); })()}
                      {jr.actioned_by_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" data-testid={`jr-actioner-${jr.id}`}>{jr.actioned_by_name}</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PRIORITY_MAP[jr.priority] || ""}`}>{jr.priority?.toUpperCase()}</span>
                      <SLAStatusBadge entity={jr} />
                      {isStale && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 flex items-center gap-0.5" data-testid={`jr-stale-${jr.id}`}><Warning size={10} /> {daysSinceUpdate}d idle</span>}
                      {isOverdue && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" data-testid={`jr-overdue-${jr.id}`}>Overdue</span>}
                    </div>
                    <p className="text-sm font-semibold truncate">{jr.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      {jr.client_name && <span className="flex items-center gap-1"><User size={12} /> {jr.client_name}</span>}
                      {jr.requester_contact_name && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: jr.requester_contact_color || "#3b82f6" }} data-testid={`jr-list-requester-tag-${jr.id}`}>
                          {jr.requester_contact_name}
                        </span>
                      )}
                      {jr.property_address && <span className="flex items-center gap-1 truncate max-w-[200px]"><MapPin size={12} /> {jr.property_address}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {jr.estimated_budget > 0 && <p className="text-xs font-semibold flex items-center gap-0.5 justify-end"><CurrencyGbp size={12} />{jr.estimated_budget.toLocaleString()}</p>}
                    <p className="text-[10px] text-muted-foreground mt-0.5">{quoteCount} quote{quoteCount !== 1 ? "s" : ""}</p>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-0.5 justify-end"><Clock size={10} /> {new Date(jr.created_at).toLocaleDateString("en-GB")}</p>
                    {jr.created_by_name && <p className="text-[10px] text-muted-foreground justify-end flex" data-testid={`jr-added-by-${jr.id}`}>Added by {jr.created_by_name}</p>}
                    {jr.price_mandate && <p className="text-[9px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 rounded px-1.5 py-0.5 inline-flex items-center gap-0.5 mt-1" data-testid={`jr-mandate-${jr.id}`}><ShieldCheck size={10} /> MANDATE £{Number(jr.mandate_limit || 0).toLocaleString()}</p>}
                  </div>
                </Link>
                <div className="shrink-0 pl-1" onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
                  {isUnclaimed(jr) ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => claimJR(jr)} data-testid={`jr-claim-${jr.id}`}>
                      <Hand size={12} className="mr-1" /> Claim
                    </Button>
                  ) : jr.actioned_by ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={`Claimed by ${jr.actioned_by_name} — hand over`} data-testid={`jr-handover-${jr.id}`}>
                          <UserSwitch size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {uniqueAdmins.filter(a => String(a._id) !== jr.actioned_by).map(a => (
                          <DropdownMenuItem key={a._id} onClick={() => handoverJR(jr, a)} data-testid={`jr-handover-to-${jr.id}-${a._id}`}>Hand to {a.name}</DropdownMenuItem>
                        ))}
                        <DropdownMenuItem className="text-red-600" onClick={() => handoverJR(jr, null)} data-testid={`jr-unclaim-${jr.id}`}>Unclaim</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="create-jr-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">New Request</DialogTitle>
            <DialogDescription>Enter the client's job request details. You can add quotes and notes after creation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {workTemplates.length > 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-md border border-dashed border-border" data-testid="jr-template-selector">
                <Bookmark size={14} className="text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Apply Template:</span>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="jr-apply-template"><SelectValue placeholder="Select a template to pre-fill..." /></SelectTrigger>
                  <SelectContent>
                    {workTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Job Title *</Label>
              <AiBriefButton
                workType="request"
                category={form.category}
                propertyAddress={form.property_address}
                onApply={(brief) => setForm(prev => ({
                  ...prev,
                  title: brief.title || prev.title,
                  description: brief.description || prev.description,
                  category: brief.suggested_category || prev.category,
                  urgency: brief.priority_suggestion || prev.urgency,
                  service_required: prev.service_required || detectService(`${brief.suggested_category || ""} ${brief.title || ""} ${brief.description || ""}`, serviceOptions) || "",
                }))}
              />
            </div>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Kitchen Refurbishment - 42 High Street" data-testid="jr-title-input" />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Client</Label>
                <Select value={form.client_id || "__none__"} onValueChange={handleSelectClient}>
                  <SelectTrigger data-testid="jr-client-select"><SelectValue placeholder="Select a client..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No client selected</SelectItem>
                    {allClients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name ? `${c.company_name} — ${c.first_name} ${c.last_name}` : `${c.first_name} ${c.last_name}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.client_id && selectedClientContacts.length > 0 && (
                <div className="col-span-2">
                  <Label>Requester Contact</Label>
                  <Select value={form.requester_contact_id || "__none__"} onValueChange={handleSelectRequester}>
                    <SelectTrigger data-testid="jr-requester-select"><SelectValue placeholder="Tag a sub-contact as requester..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No specific requester</SelectItem>
                      {selectedClientContacts.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full shrink-0 inline-block" style={{ backgroundColor: c.color || "#3b82f6" }} />
                            {c.first_name} {c.last_name}{c.job_role ? ` (${c.job_role})` : ""}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.client_id && (
                <div className="col-span-2 grid grid-cols-2 gap-3 border border-border rounded-md p-3 bg-muted/20">
                  <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Name</Label><p className="text-sm font-medium mt-0.5">{form.client_name || "—"}</p></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Company</Label><p className="text-sm font-medium mt-0.5">{form.client_company || "—"}</p></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</Label><p className="text-sm font-medium mt-0.5">{form.client_email || "—"}</p></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone</Label><p className="text-sm font-medium mt-0.5">{form.client_phone || "—"}</p></div>
                  {form.requester_contact_name && (
                    <div className="col-span-2"><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Requester</Label>
                      <span className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: form.requester_contact_color || "#3b82f6" }} data-testid="jr-requester-tag">{form.requester_contact_name}</span>
                    </div>
                  )}
                </div>
              )}
              {!form.client_id && (
                <>
                  <div><Label>Client Name</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="John Smith" data-testid="jr-client-name" /></div>
                  <div><Label>Client Company</Label><Input value={form.client_company} onChange={e => setForm({ ...form, client_company: e.target.value })} placeholder="Smith Properties Ltd" /></div>
                  <div><Label>Client Email</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="john@smith.com" data-testid="jr-client-email" /></div>
                  <div><Label>Client Phone</Label><Input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} placeholder="+44 7..." /></div>
                </>
              )}
            </div>
            <div className="mb-3">
              <ClientPropertyPicker clientId={form.client_id} clientLabel={form.client_company || form.client_name}
                propertyId={form.property_id} addToPortfolio={form.add_to_portfolio} onToggleAdd={(v) => setForm(f => ({ ...f, add_to_portfolio: v }))}
                canAdd={!!(form.street_address && form.postcode) || !!form.property_address} testid="jr-client-property"
                onPick={(p) => setForm(f => p ? ({ ...f, property_id: p.id, property_name: p.name || "",
                  property_address: [p.address_line, p.town_city, p.county, p.postcode].filter(Boolean).join(", "),
                  street_address: p.address_line || "", town_city: p.town_city || "", county: p.county || "", postcode: p.postcode || "", what3words: p.what3words || f.what3words,
                  site_contact_name: p.site_contact_name || f.site_contact_name, site_contact_phone: p.site_contact_phone || f.site_contact_phone, site_contact_email: p.site_contact_email || f.site_contact_email,
                  access_notes: p.access_notes || f.access_notes, add_to_portfolio: false }) : ({ ...f, property_id: "", property_name: "" }))} />
            </div>
            <div>
              <Label>Property Address *</Label>
              <AddressAutocomplete
                value={form.property_address}
                onChange={v => setForm({ ...form, property_address: v })}
                onSelect={(a) => setForm(f => ({
                  ...f,
                  property_address: a.formatted_address || f.property_address,
                  street_address: a.street_address || "",
                  town_city: a.town_city || "",
                  county: a.county || "",
                  postcode: a.postcode || "",
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                placeholder="42 High Street, London, SW1A 1AA"
                data-testid="jr-address"
              />
            </div>
            <div>
              <Label>what3words <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
              <Input value={form.what3words} onChange={e => setForm({ ...form, what3words: e.target.value })} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="jr-what3words" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">SITE / PROPERTY CONTACT</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Site Contact</Label><Input value={form.site_contact_name} onChange={e => setForm({ ...form, site_contact_name: e.target.value })} placeholder="E.G Tenant" className="mt-1" data-testid="jr-site-contact-name" /></div>
                <div><Label>Phone</Label><Input value={form.site_contact_phone} onChange={e => setForm({ ...form, site_contact_phone: e.target.value })} placeholder="+44 7..." className="mt-1" data-testid="jr-site-contact-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.site_contact_email} onChange={e => setForm({ ...form, site_contact_email: e.target.value })} placeholder="tenant@email.com" className="mt-1" data-testid="jr-site-contact-email" /></div>
              </div>
            </div>
            <AccessNotesField value={form.access_notes} onChange={(v) => setForm(f => ({ ...f, access_notes: v }))} propertyLinked={!!form.property_id} testid="jr-access-notes" />
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="jr-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="jr-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Target Date</Label><Input type="date" value={form.requested_completion_date} onChange={e => setForm({ ...form, requested_completion_date: e.target.value })} /></div>
            </div>
            {form.client_id && (
              <RepeatFaultBanner endpoint={`/clients/${form.client_id}/repeat-check`} category={form.category} propertyId={form.property_id}
                addressLine={form.street_address || splitFlatAddress(form.property_address).address_line} postcode={form.postcode || splitFlatAddress(form.property_address).postcode}
                testid="jr-repeat-fault-banner" />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Service Required</Label>
                <Select value={form.service_required || "__none__"} onValueChange={v => { setServiceManual(true); setServiceSuggested(false); setForm({ ...form, service_required: v === "__none__" ? "" : v }); }}>
                  <SelectTrigger data-testid="jr-service-required"><SelectValue placeholder="e.g. Electrical, Plumbing..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not specified</SelectItem>
                    {serviceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {serviceSuggested ? (
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-1 font-medium" data-testid="jr-service-suggested-hint">✦ Auto-suggested from the description — change it if it's wrong</p>
                ) : (
                  <p className="text-[10px] text-muted-foreground mt-1">Used to surface the most relevant area-matched suppliers</p>
                )}
              </div>
              <div>
                <Label>Estimated Budget (£)</Label>
                <Input type="number" step="0.01" value={form.estimated_budget} onChange={e => setForm({ ...form, estimated_budget: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Request Details</Label>
              <Textarea value={form.request_details} onChange={e => setForm({ ...form, request_details: e.target.value })} rows={4} placeholder="Describe the work required in detail..." data-testid="jr-details" />
            </div>
            <ScopeOfWorksField value={form.scope_of_works} onChange={v => setForm(f => ({ ...f, scope_of_works: v }))} testid="jr-scope-of-works"
              context={{ entity: "request", title: form.title, description: form.request_details, client_name: form.client_company || form.client_name, property_address: form.property_address }} />
            <NoticeEditor value={form.notice} onChange={(n) => setForm({ ...form, notice: n })} clientToggle aiContext={{ entity: "job_request", title: form.title, description: form.description }} testid="jr-notice" compact />
            <div>
              <Label>Images / Files</Label>
              <div className="mt-1">
                <FileDropZone
                  files={formAttachments}
                  onFilesChange={setFormAttachments}
                  folder="job-requests"
                  accept="all"
                  maxFiles={10}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="jr-submit-btn">{creating ? "Creating..." : "Create Request"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
